import { Button, Grid, InputAdornment, Switch, TextField } from '@material-ui/core';
import { Close, LockOpen, MonetizationOn, Save, Toc, VerifiedUser } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IsNullOrEmpty } from '../../helpers/utility';
import { fetchSettingsRequest, updateSettingRequest } from '../../store/setting/setting.actions';
import { getSetting } from '../../store/setting/setting.selector';
import Loader from '../base/loader';

const WITHDRAW_SPLIT_TOTAL = 100;

const isValidPercentage = (value) => {
    if (IsNullOrEmpty(value)) return false;
    const parsed = parseFloat(value);
    return isFinite(parsed) && parsed >= 0 && parsed <= 100;
};

const toPercent = (value) => {
    const parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
};

// Withdrawal limits are optional, and an empty box means the same as 0: no limit on that side.
const toAmount = (value) => {
    const parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
};

// Existing settings documents pre-date the withdraw split, so treat them as 100% USDT.
const readWithdrawSplit = (setting) => {
    const usdt = setting.usdtWithdrawPercentage;
    const token = setting.tokenWithdrawPercentage;
    const isConfigured = (usdt !== undefined && usdt !== null) || (token !== undefined && token !== null);
    return {
        usdt: isConfigured ? (usdt || 0) : WITHDRAW_SPLIT_TOTAL,
        token: isConfigured ? (token || 0) : 0,
    };
};

const shortAddress = (value) => {
    if (!value) return "Not configured";
    const text = value.toString();
    return text.length > 18 ? `${text.slice(0, 10)}…${text.slice(-6)}` : text;
};

function SectionHeading({ number, icon, title, description }) {
    return (
        <div className="settings-section-heading">
            <span className="settings-section-icon">{icon}</span>
            <div className="settings-section-copy">
                <span className="settings-section-number">Section {number}</span>
                <h2>{title}</h2>
                <p>{description}</p>
            </div>
        </div>
    );
}

export default function SettingContainer() {
    const dispatch = useDispatch();
    const [minAmount, setMinAmount] = React.useState(0);
    const [maxAmount, setMaxAmount] = React.useState(0);
    const [minWithdrawAmount, setMinWithdrawAmount] = React.useState(0);
    const [maxWithdrawAmount, setMaxWithdrawAmount] = React.useState(0);
    const [tokenAddress, setTokenAddress] = React.useState("");
    const [tokenSymbol, setTokenSymbol] = React.useState("");
    const [tokenName, setTokenName] = React.useState("");
    const [tokenDecimals, setTokenDecimals] = React.useState(18);
    const [tokenPrice, setTokenPrice] = React.useState(0);
    const [stackingAddress, setStackingAddress] = React.useState("");
    const [withdrawApproval, setWithdrawApproval] = React.useState(false);
    const [thresholdDays, setThresholdDays] = React.useState(0);
    const [returns, setReturns] = React.useState(0);
    const [usdtWithdrawPercentage, setUsdtWithdrawPercentage] = React.useState(0);
    const [tokenWithdrawPercentage, setTokenWithdrawPercentage] = React.useState(0);
    const [nomineeList, setNomineeList] = React.useState("")
    const [isValidated, setValidated] = useState(false);

    const { isLoading, setting } = useSelector((state) => {
        return {
            setting: getSetting(state),
            isLoading: state.UserReducer.isLoading
        }
    }, []);

    const reset = () => {
        if (setting._id) {
            setMinAmount(setting.minAmount);
            setMaxAmount(setting.maxAmount)
            // Settings saved before these existed carry neither, and both read as "no limit".
            setMinWithdrawAmount(setting.minWithdrawAmount || 0)
            setMaxWithdrawAmount(setting.maxWithdrawAmount || 0)
            setThresholdDays(setting.thresholdDays)
            setReturns(setting.returns)
            setUsdtWithdrawPercentage(readWithdrawSplit(setting).usdt)
            setTokenWithdrawPercentage(readWithdrawSplit(setting).token)
            setNomineeList(setting.nomineeList)
            setWithdrawApproval(setting.withdrawApproval);
            setTokenAddress(setting.tokenAddress);
            setTokenDecimals(setting.tokenDecimals)
            setTokenName(setting.tokenName)
            setTokenPrice(setting.tokenPrice)
            setTokenSymbol(setting.tokenSymbol)
            setStackingAddress(setting.stackingAddress)
            setValidated(false);
        }
    }

    useEffect(() => {
        if (setting._id) {
            setMinAmount(setting.minAmount);
            setMaxAmount(setting.maxAmount)
            // Settings saved before these existed carry neither, and both read as "no limit".
            setMinWithdrawAmount(setting.minWithdrawAmount || 0)
            setMaxWithdrawAmount(setting.maxWithdrawAmount || 0)
            setThresholdDays(setting.thresholdDays)
            setReturns(setting.returns)
            setUsdtWithdrawPercentage(readWithdrawSplit(setting).usdt)
            setTokenWithdrawPercentage(readWithdrawSplit(setting).token)
            setNomineeList(setting.nomineeList)
            setTokenAddress(setting.tokenAddress);
            setWithdrawApproval(setting.withdrawApproval);
            setTokenDecimals(setting.tokenDecimals)
            setTokenName(setting.tokenName)
            setTokenPrice(setting.tokenPrice)
            setTokenSymbol(setting.tokenSymbol)
            setStackingAddress(setting.stackingAddress)
        }
    }, [setting])

    useEffect(() => {
        dispatch(fetchSettingsRequest())
    }, [])

    const splitTotal = Math.round((toPercent(usdtWithdrawPercentage) + toPercent(tokenWithdrawPercentage)) * 100) / 100;
    const isSplitBalanced = isValidPercentage(usdtWithdrawPercentage)
        && isValidPercentage(tokenWithdrawPercentage)
        && splitTotal === WITHDRAW_SPLIT_TOTAL;

    const minWithdrawLimit = toAmount(minWithdrawAmount);
    const maxWithdrawLimit = toAmount(maxWithdrawAmount);
    // 0 is "no limit on that side", so only a pair that is genuinely set can be out of order — and
    // a ceiling under the floor would refuse every withdrawal there is.
    const isWithdrawLimitValid = minWithdrawLimit >= 0 && maxWithdrawLimit >= 0
        && !(minWithdrawLimit > 0 && maxWithdrawLimit > 0 && maxWithdrawLimit < minWithdrawLimit);

    const validate = () => {
        return isWithdrawLimitValid &&
            !IsNullOrEmpty(minAmount) && parseFloat(minAmount) > 0 &&
            !IsNullOrEmpty(maxAmount) && parseFloat(maxAmount) > 0 &&
            !IsNullOrEmpty(thresholdDays) && parseFloat(thresholdDays) > 0 &&
            !IsNullOrEmpty(returns) && parseFloat(returns) > 0 &&
            isSplitBalanced &&
            !IsNullOrEmpty(nomineeList) &&
            !IsNullOrEmpty(tokenDecimals) && parseFloat(tokenDecimals) > 0 &&
            !IsNullOrEmpty(tokenSymbol) &&
            !IsNullOrEmpty(tokenPrice) && parseFloat(tokenPrice) > 0 &&
            !IsNullOrEmpty(tokenAddress) &&
            !IsNullOrEmpty(tokenName) &&
            !IsNullOrEmpty(stackingAddress)
    }

    const handleSave = () => {
        if (validate()) {
            dispatch(updateSettingRequest({
                minAmount: minAmount,
                maxAmount: maxAmount,
                // Always sent, and an emptied box goes out as 0 — that is how a limit gets removed
                // again, since the service leaves a field it is not given alone.
                minWithdrawAmount: minWithdrawLimit,
                maxWithdrawAmount: maxWithdrawLimit,
                thresholdDays: thresholdDays,
                returns: returns,
                usdtWithdrawPercentage: usdtWithdrawPercentage,
                tokenWithdrawPercentage: tokenWithdrawPercentage,
                nomineeList: nomineeList,
                withdrawApproval: withdrawApproval,
                tokenDecimals: tokenDecimals,
                tokenName: tokenName,
                tokenPrice: tokenPrice,
                tokenSymbol: tokenSymbol,
                tokenAddress: tokenAddress,
                stackingAddress: stackingAddress,
            }))
        }
        else {
            setValidated(true)
        }
    }

    const current = {
        minAmount, maxAmount, thresholdDays, returns, nomineeList, withdrawApproval,
        usdtWithdrawPercentage, tokenWithdrawPercentage,
        minWithdrawAmount: minWithdrawLimit, maxWithdrawAmount: maxWithdrawLimit,
        tokenAddress, tokenName, tokenSymbol, tokenDecimals, tokenPrice, stackingAddress,
    };
    const savedValues = {
        ...setting,
        usdtWithdrawPercentage: readWithdrawSplit(setting).usdt,
        tokenWithdrawPercentage: readWithdrawSplit(setting).token,
        // Normalised the same way the state is, or a document saved before these fields existed
        // would read as an unsaved change on every load.
        minWithdrawAmount: setting.minWithdrawAmount || 0,
        maxWithdrawAmount: setting.maxWithdrawAmount || 0,
    };
    const isDirty = !!setting._id && Object.keys(current).some((key) => {
        const saved = savedValues[key];
        if (typeof current[key] === "boolean" || typeof saved === "boolean") {
            return !!current[key] !== !!saved;
        }
        return `${current[key] === undefined || current[key] === null ? "" : current[key]}`
            !== `${saved === undefined || saved === null ? "" : saved}`;
    });

    const nomineeCount = (nomineeList || "").split(",").map((x) => x.trim()).filter(Boolean).length;

    return (
        <div className="settings-page">
            {
                isLoading && <Loader />
            }

            <div className="settings-shell">
                <div className="settings-intro">
                    <div className="settings-intro-copy">
                        <span className="settings-kicker">Control center</span>
                        <h1>Application settings</h1>
                        <p>Configure transaction limits, token metadata, and operational approvals.</p>
                    </div>
                    <div className={`settings-status ${isDirty ? "is-dirty" : ""}`}>
                        <span />
                        {isDirty ? "Unsaved changes" : "All changes saved"}
                    </div>
                </div>

                <div className="settings-overview">
                    <div className="settings-overview-item">
                        <span>Transaction range</span>
                        <strong>{minAmount || 0} – {maxAmount || 0} USD</strong>
                    </div>
                    <div className="settings-overview-item">
                        <span>Per withdrawal</span>
                        <strong>
                            {
                                minWithdrawLimit > 0 || maxWithdrawLimit > 0
                                    ? `${minWithdrawLimit > 0 ? minWithdrawLimit : "Any"} – ${maxWithdrawLimit > 0 ? maxWithdrawLimit : "Any"} USD`
                                    : "Not limited"
                            }
                        </strong>
                    </div>
                    <div className="settings-overview-item">
                        <span>Token</span>
                        <strong>{tokenSymbol || "—"} · {tokenPrice || 0} USD</strong>
                    </div>
                    <div className="settings-overview-item">
                        <span>Withdraw split</span>
                        <strong className={isSplitBalanced ? "" : "is-on"}>
                            {toPercent(usdtWithdrawPercentage)}% USDT · {toPercent(tokenWithdrawPercentage)}% Token
                        </strong>
                    </div>
                    <div className="settings-overview-item">
                        <span>Nominees</span>
                        <strong>{nomineeCount} configured</strong>
                    </div>
                    <div className="settings-overview-item">
                        <span>Withdraw approval</span>
                        <strong className={withdrawApproval ? "is-on" : "is-off"}>
                            {withdrawApproval ? "Manual" : "Automatic"}
                        </strong>
                    </div>
                </div>

                <Grid container className='settings-grid'>
                    <Grid item md={6} lg={6} sm={12} xs={12}>
                        <div className="settings-section">
                            <SectionHeading
                                number="01"
                                icon={<MonetizationOn />}
                                title="Transaction policy"
                                description="Limits, returns, and nominee configuration."
                            />
                            <Grid container className="settings-fields">
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Min Amount"
                                        placeholder='Min Amount'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={minAmount}
                                        onChange={(e) => setMinAmount(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>USD</InputAdornment> }}
                                        helperText={isValidated && (IsNullOrEmpty(minAmount) || parseFloat(minAmount) <= 0) ? "Please enter min amount" : ""}
                                        error={isValidated && (IsNullOrEmpty(minAmount) || parseFloat(minAmount) <= 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Max Amount"
                                        placeholder='Max Amount'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={maxAmount}
                                        onChange={(e) => setMaxAmount(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>USD</InputAdornment> }}
                                        helperText={isValidated && (IsNullOrEmpty(maxAmount) || parseFloat(maxAmount) <= 0) ? "Please enter max amount" : ""}
                                        error={isValidated && (IsNullOrEmpty(maxAmount) || parseFloat(maxAmount) <= 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Min Withdraw Amount"
                                        placeholder='Min Withdraw Amount'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={minWithdrawAmount}
                                        onChange={(e) => setMinWithdrawAmount(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>USD</InputAdornment> }}
                                        helperText={
                                            isValidated && minWithdrawLimit < 0
                                                ? "Please enter a positive amount"
                                                : "Smallest single withdrawal · 0 for no minimum"
                                        }
                                        error={isValidated && (minWithdrawLimit < 0 || !isWithdrawLimitValid)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Max Withdraw Amount"
                                        placeholder='Max Withdraw Amount'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={maxWithdrawAmount}
                                        onChange={(e) => setMaxWithdrawAmount(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>USD</InputAdornment> }}
                                        helperText={
                                            isValidated && !isWithdrawLimitValid
                                                ? "Max withdraw must be at least the min withdraw"
                                                : "Largest single withdrawal · 0 for no maximum"
                                        }
                                        error={isValidated && !isWithdrawLimitValid}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <p className="settings-hint">
                                        These cap one withdrawal request. Min / Max Amount above stay
                                        the payout window — Max Amount is the total a member can be
                                        paid across the threshold days.
                                    </p>
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Threshold Days"
                                        placeholder='Threshold Days'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={thresholdDays}
                                        onChange={(e) => setThresholdDays(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>Days</InputAdornment> }}
                                        helperText={isValidated && (IsNullOrEmpty(thresholdDays) || parseFloat(thresholdDays) <= 0) ? "Please enter the days" : ""}
                                        error={isValidated && (IsNullOrEmpty(thresholdDays) || parseFloat(thresholdDays) <= 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Returns"
                                        placeholder='Returns'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={returns}
                                        onChange={(e) => setReturns(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                                        helperText={isValidated && (IsNullOrEmpty(returns) || parseFloat(returns) <= 0) ? "Please enter the returns" : ""}
                                        error={isValidated && (IsNullOrEmpty(returns) || parseFloat(returns) <= 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="USDT Withdraw Percentage"
                                        placeholder='USDT Withdraw Percentage'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={usdtWithdrawPercentage}
                                        onChange={(e) => setUsdtWithdrawPercentage(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                                        helperText="Share of a withdrawal settled in USDT"
                                        error={isValidated && !isSplitBalanced}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Token Withdraw Percentage"
                                        placeholder='Token Withdraw Percentage'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={tokenWithdrawPercentage}
                                        onChange={(e) => setTokenWithdrawPercentage(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                                        helperText="Share of a withdrawal settled in tokens"
                                        error={isValidated && !isSplitBalanced}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <div className={`settings-split ${isSplitBalanced ? "is-balanced" : "is-unbalanced"}`}>
                                        <div className="settings-split-bar">
                                            <span
                                                className="settings-split-seg settings-split-usdt"
                                                style={{ width: `${Math.min(100, Math.max(0, toPercent(usdtWithdrawPercentage)))}%` }}
                                            />
                                            <span
                                                className="settings-split-seg settings-split-token"
                                                style={{ width: `${Math.min(100, Math.max(0, toPercent(tokenWithdrawPercentage)))}%` }}
                                            />
                                        </div>
                                        <div className="settings-split-meta">
                                            <span className="settings-split-legend">
                                                <span className="settings-split-dot settings-split-usdt" />USDT {toPercent(usdtWithdrawPercentage)}%
                                                <span className="settings-split-dot settings-split-token" />Token {toPercent(tokenWithdrawPercentage)}%
                                            </span>
                                            <span className="settings-split-total">
                                                Total {splitTotal}%
                                                {
                                                    !isSplitBalanced &&
                                                    <button
                                                        type="button"
                                                        className="settings-split-fix"
                                                        onClick={() => setTokenWithdrawPercentage(
                                                            Math.min(WITHDRAW_SPLIT_TOTAL, Math.max(0,
                                                                Math.round((WITHDRAW_SPLIT_TOTAL - toPercent(usdtWithdrawPercentage)) * 100) / 100))
                                                        )}
                                                    >
                                                        Balance to 100%
                                                    </button>
                                                }
                                            </span>
                                        </div>
                                        {
                                            !isSplitBalanced &&
                                            <span className="settings-split-error">
                                                USDT and token withdraw percentages must add up to 100%.
                                            </span>
                                        }
                                    </div>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Nominees"
                                        placeholder='Nominee List'
                                        variant="outlined"
                                        multiline
                                        minRows={4}
                                        fullWidth
                                        size="small"
                                        value={nomineeList}
                                        onChange={(e) => setNomineeList(e.target.value)}
                                        helperText={isValidated && IsNullOrEmpty(nomineeList) ? "Please enter nominees" : `Comma separated list · ${nomineeCount} entries`}
                                        error={isValidated && IsNullOrEmpty(nomineeList)}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>

                    <Grid item md={6} lg={6} sm={12} xs={12}>
                        <div className="settings-section">
                            <SectionHeading
                                number="02"
                                icon={<Toc />}
                                title="Token configuration"
                                description="Contract metadata and settlement addresses."
                            />
                            <div className="settings-token-preview">
                                <span className="settings-token-badge">{(tokenSymbol || "T").slice(0, 3).toUpperCase()}</span>
                                <div className="settings-token-copy">
                                    <strong>{tokenName || "Token name"}</strong>
                                    <span>{shortAddress(tokenAddress)}</span>
                                </div>
                                <div className="settings-token-meta">
                                    <span>{tokenDecimals || 0} decimals</span>
                                    <strong>{tokenPrice || 0} USD</strong>
                                </div>
                            </div>
                            <Grid container className="settings-fields">
                                <Grid item xs={12}>
                                    <TextField
                                        label="Token Address"
                                        placeholder='Token Address'
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        value={tokenAddress}
                                        onChange={(e) => setTokenAddress(e.target.value)}
                                        helperText={isValidated && IsNullOrEmpty(tokenAddress) ? "Please enter Token Address" : ""}
                                        error={isValidated && IsNullOrEmpty(tokenAddress)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Token Name"
                                        placeholder='Token Name'
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        value={tokenName}
                                        onChange={(e) => setTokenName(e.target.value)}
                                        helperText={isValidated && (IsNullOrEmpty(tokenName)) ? "Please enter the name" : ""}
                                        error={isValidated && (IsNullOrEmpty(tokenName))}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Token Symbol"
                                        placeholder='Token Symbol'
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        value={tokenSymbol}
                                        onChange={(e) => setTokenSymbol(e.target.value)}
                                        helperText={isValidated && (IsNullOrEmpty(tokenSymbol)) ? "Please enter the symbol" : ""}
                                        error={isValidated && (IsNullOrEmpty(tokenSymbol))}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Token Decimals"
                                        placeholder='Token Decimals'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={tokenDecimals}
                                        onChange={(e) => setTokenDecimals(e.target.value)}
                                        helperText={isValidated && (IsNullOrEmpty(tokenDecimals) || parseFloat(tokenDecimals) <= 0) ? "Please enter Token Decimals" : ""}
                                        error={isValidated && (IsNullOrEmpty(tokenDecimals) || parseFloat(tokenDecimals) <= 0)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                    <TextField
                                        label="Token Price"
                                        placeholder='Token Price'
                                        variant="outlined"
                                        type='number'
                                        fullWidth
                                        size="small"
                                        value={tokenPrice}
                                        onChange={(e) => setTokenPrice(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position='end'>USD</InputAdornment> }}
                                        helperText={isValidated && (IsNullOrEmpty(tokenPrice) || parseFloat(tokenPrice) <= 0) ? "Please enter Token Price" : ""}
                                        error={isValidated && (IsNullOrEmpty(tokenPrice) || parseFloat(tokenPrice) <= 0)}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Stacking Address"
                                        placeholder='Stacking Address'
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        value={stackingAddress}
                                        onChange={(e) => setStackingAddress(e.target.value)}
                                        helperText={isValidated && IsNullOrEmpty(stackingAddress) ? "Please enter Stacking Address" : ""}
                                        error={isValidated && IsNullOrEmpty(stackingAddress)}
                                    />
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>

                    <Grid item md={12} lg={12} sm={12} xs={12}>
                        <div className="settings-section">
                            <SectionHeading
                                number="03"
                                icon={<VerifiedUser />}
                                title="Operational controls"
                                description="Approval behaviour applied to member withdrawals."
                            />
                            <div className={`settings-toggle ${withdrawApproval ? "is-on" : ""}`}>
                                <span className="settings-toggle-icon"><LockOpen /></span>
                                <div className="settings-toggle-copy">
                                    <strong>Withdraw Approval Required</strong>
                                    <span>
                                        {withdrawApproval
                                            ? "Every withdrawal waits for an admin to approve it before settlement."
                                            : "Withdrawals are settled automatically without manual approval."}
                                    </span>
                                </div>
                                <Switch
                                    checked={!!withdrawApproval}
                                    onChange={(e) => setWithdrawApproval(e.target.checked)}
                                    name="Withdraw Approval Required"
                                />
                            </div>
                        </div>
                    </Grid>
                </Grid>

                <div className="settings-actions">
                    <span className="settings-actions-note">
                        {isDirty ? "You have unsaved configuration changes." : "Configuration matches the saved settings."}
                    </span>
                    <div className="settings-actions-buttons">
                        <Button className="settings-btn-ghost" onClick={() => reset()} startIcon={<Close />}>
                            Discard Changes
                        </Button>
                        <Button className="settings-btn-primary" onClick={() => handleSave()} startIcon={<Save />}>
                            Save Changes
                        </Button>
                    </div>
                </div>
            </div>
        </div >
    );
}
